import {UserDefinedObjectTemplateAdditionalColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {resolveGeographyAdditionalColumnValue} from "~/utilities/geographyValueResolver";
import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import {AmaronOus} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/amaron/OrganizationalUnits";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {OttopilotEventContainer} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import {getPathFromLeaf} from "~/utilities/accessControl/ouHierarchyDisplay";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import {getOptionalEventContainerOrganizationalUnitId} from "~/utilities/ottopilotSemanticColumns";

export function getAmaronLeafOuTemplateDisplayName(
    organizationalUnitTemplates: ReadonlyArray<OrganizationalUnitTemplate>,
): string {
    const template = organizationalUnitTemplates.find(
        (item) => item.id === (AmaronOus.Pincode as Uuid),
    );
    if (template !== undefined) {
        return template.displayName;
    }
    return "Pincode";
}

export function getAmaronJourneyListCardAssignedOuColumnLabel(
    journey: OttopilotEventContainer,
    eventContainerTemplate: EventContainerTemplate,
    organizationalUnits: ReadonlyArray<OrganizationalUnit>,
    organizationalUnitTemplates: ReadonlyArray<OrganizationalUnitTemplate>,
): string {
    if (eventContainerTemplate.configuration.organizationalUnitColumnConfig === null) {
        return "State";
    }
    const organizationalUnitId = getOptionalEventContainerOrganizationalUnitId(journey, eventContainerTemplate);
    if (organizationalUnitId === null) {
        return getAmaronLeafOuTemplateDisplayName(organizationalUnitTemplates);
    }
    const path = getPathFromLeaf(
        organizationalUnitId,
        [...organizationalUnits],
        [...organizationalUnitTemplates],
    );
    const leafEntry = path[path.length - 1];
    if (leafEntry !== undefined && leafEntry.type.trim() !== "") {
        return leafEntry.type;
    }
    return getAmaronLeafOuTemplateDisplayName(organizationalUnitTemplates);
}

export function getAmaronJourneyListCardAssignedOuName(
    journey: OttopilotEventContainer,
    eventContainerTemplate: EventContainerTemplate,
    organizationalUnits: ReadonlyArray<OrganizationalUnit>,
    organizationalUnitTemplates: ReadonlyArray<OrganizationalUnitTemplate>,
): string | null {
    if (eventContainerTemplate.configuration.organizationalUnitColumnConfig === null) {
        const columnId = eventContainerTemplate.configuration.uacmGeographyColumnId;
        if (columnId === undefined) {throw new Error("Amaron State geography is not configured");}
        const id = journey.additionalColumnValues[columnId];
        if (typeof id !== "string" || id.trim() === "") {return null;}
        const name = resolveGeographyAdditionalColumnValue(UserDefinedObjectTemplateAdditionalColumnType.geographyIndiaStates, id);
        return name === null || name === "-" ? id : name;
    }
    const organizationalUnitId = getOptionalEventContainerOrganizationalUnitId(journey, eventContainerTemplate);
    if (organizationalUnitId === null) {
        return null;
    }
    const ou = organizationalUnits.find((item) => item.id === organizationalUnitId);
    if (ou === undefined) {
        return null;
    }
    const template = organizationalUnitTemplates.find((item) => item.id === ou.templateId);
    if (template === undefined) {
        return null;
    }
    const value = processOttopilotStringTemplate(
        template.instanceDisplayNameTemplate,
        ou.additionalColumnValues,
    );
    return value.trim() === "" ? null : value;
}
